import React, { useEffect, useState, useCallback, memo } from "react";
import CustomDatepicker from "./Datepicker";
import "./datepicker.css";

interface Props {
  startName?: string;
  endName?: string;
  startValue?: string;
  endValue?: string;
  required?: boolean;
  helperText?: string;
  disabled?: boolean;
  error?: string;
  startLabelName: string;
  endLabelName: string;
  endDatePlaceholder?: string;
  startDatePlaceholder?: string;
  maxDate?: Date;
  showInlineError?: boolean;
  onChange: (start: string | undefined, end: string | undefined) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onDateError?: (isDateError: boolean) => void;

}

const DateRange: React.FC<Props> = ({
  startName = "startDate",
  endName = "endDate",
  startLabelName = "Start date",
  endLabelName = "End date",
  startValue = "",
  endValue = "",
  required,
  helperText,
  disabled,
  error,
  startDatePlaceholder,
  endDatePlaceholder,
  maxDate,
  showInlineError = false,
  onChange,
  onDateError,
  onBlur,

}) => {
  const [start, setStart] = useState<string | undefined>(
    startValue || undefined,
  );
  const [end, setEnd] = useState<string | undefined>(endValue || undefined);
  const [internalError, setInternalError] = useState<string | null>(null);

  useEffect(() => {
    setStart(startValue || undefined);
    setEnd(endValue || undefined);
  }, [startValue, endValue]);

  function clearDateError() {
    setInternalError(null);
    onDateError?.(false);
  }

  useEffect(() => {
    const normalize = (s?: string) => (s ? s.trim() : undefined);
    const sVal = normalize(start);
    const eVal = normalize(end);

    const parseToUTC = (s: string): number | null => {
      // Expect MM/DD/YYYY
      const m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(s);
      if (!m) return null;
      const mm = Number.parseInt(m[1], 10);
      const dd = Number.parseInt(m[2], 10);
      const yy = Number.parseInt(m[3], 10);
      if (Number.isNaN(mm) || Number.isNaN(dd) || Number.isNaN(yy)) return null;
      // basic bounds
      if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
      return Date.UTC(yy, mm - 1, dd);
    };

    if (sVal && eVal) {
      const sTs = parseToUTC(sVal);
      const eTs = parseToUTC(eVal);
      if (sTs === null || eTs === null) {
        // invalid formatted date -> clear internal error (leave external validation to parent)
        clearDateError();
      } else if (sTs > eTs) {
        setInternalError("Start date must be before or equal to end date");
        onDateError?.(true);
      } else {
        clearDateError();
      }
    } else {
      clearDateError();
    }

    onChange(start, end);
  }, [start, end, onChange, onDateError]);

  const handleChange = useCallback(
    (fieldName: string, value: string) => {
      if (fieldName === startName) setStart(value || undefined);
      if (fieldName === endName) setEnd(value || undefined);
    },
    [startName, endName],
  );

  return (
    <div className="date-range-wrapper" data-testid="date-range">
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <CustomDatepicker
          name={startName}
          label={startLabelName}
          value={start ?? ""}
          required={required}
          helperText={helperText}
          disabled={disabled}
          error={internalError ?? ""}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={startDatePlaceholder}
          maxDate={maxDate}
          showInlineError={showInlineError}
        />
        <CustomDatepicker
          name={endName}
          label={endLabelName}
          value={end ?? ""}
          required={required}
          helperText={helperText}
          disabled={disabled}
          error={internalError ?? ""}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={endDatePlaceholder}
          maxDate={maxDate}
          showInlineError={showInlineError}
        />
      </div>
      {error && !internalError ? (
        <div
          className="error-description"
          data-testid="date-range-external-error"
        >
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default memo(DateRange);